'use client'

import { useComparison } from '@/lib/comparison-context'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { GradientButton } from '@/components/ui/gradient-button'
import { Badge } from '@/components/ui/badge'
import { X, Check, Minus, ChevronRight } from 'lucide-react'
import Image from 'next/image'
import { cn } from '@/lib/utils'

export function ProductComparison() {
  const { comparedProducts, removeFromComparison, clearComparison, isComparisonOpen, setIsComparisonOpen } = useComparison()

  if (comparedProducts.length === 0) {
    return null
  }

  return (
    <Sheet open={isComparisonOpen} onOpenChange={setIsComparisonOpen}>
      <SheetContent 
        side="right" 
        className="w-full sm:max-w-2xl md:max-w-3xl lg:max-w-5xl xl:max-w-6xl p-0 overflow-hidden"
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <SheetHeader className="px-6 py-4 border-b border-slate-200 bg-white sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div>
                <SheetTitle className="text-xl font-bold">
                  Compare Products
                  <Badge variant="secondary" className="ml-3">
                    {comparedProducts.length} of 4
                  </Badge>
                </SheetTitle>
                <p className="text-sm text-slate-500 mt-1">
                  Compare specifications side-by-side
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={clearComparison}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
              >
                Clear All
              </Button>
            </div>
          </SheetHeader>

          {/* Comparison Content */}
          <div className="flex-1 overflow-auto">
            {/* Mobile View - Stacked Cards */}
            <div className="lg:hidden p-4 space-y-4">
              {comparedProducts.map((product) => (
                <div key={product.id} className="bg-white rounded-lg border border-slate-200 p-4">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="relative w-20 h-20 bg-slate-100 rounded-md overflow-hidden flex-shrink-0">
                      <Image
                        src={product.images[0] || '/placeholder.svg'}
                        alt={product.title}
                        fill
                        className="object-contain p-2"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm line-clamp-2">{product.title}</h3>
                      <p className="text-xs text-slate-500 mt-1">{product.manufacturer}</p>
                      <p className="text-sm font-bold text-[#518BC4] mt-2">
                        ${product.price.toFixed(2)}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFromComparison(product.id)}
                      className="flex-shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Specifications */}
                  <div className="space-y-2 text-sm">
                    <SpecRow label="Part Number" value={product.partNumber} />
                    <SpecRow label="Category" value={product.category} />
                    <SpecRow label="Package" value={product.packageType} />
                    <SpecRow label="Stock" value={product.stock} highlighted />
                    <SpecRow label="MOQ" value={`${product.moq} units`} />
                    <SpecRow label="Lead Time" value={product.leadTime} />
                    <SpecRow label="RoHS" value={product.rohs ? 'Yes' : 'No'} />
                    <SpecRow label="Lifecycle" value={product.lifecycle} />
                    
                    {/* Additional Specifications */}
                    {Object.entries(product.specifications).slice(0, 5).map(([key, value]) => (
                      <SpecRow key={key} label={key} value={String(value)} />
                    ))}
                  </div>

                  <GradientButton
                    variant="blue-red"
                    size="sm"
                    className="w-full mt-4"
                  >
                    View Details
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </GradientButton>
                </div>
              ))}
            </div>

            {/* Desktop View - Comparison Table */}
            <div className="hidden lg:block">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="sticky left-0 bg-slate-50 px-6 py-3 text-left text-sm font-semibold text-slate-900 min-w-[200px] z-10">
                        Specification
                      </th>
                      {comparedProducts.map((product) => (
                        <th key={product.id} className="px-4 py-3 text-center min-w-[220px]">
                          <div className="flex flex-col items-center gap-2">
                            <div className="relative w-24 h-24 bg-white rounded-md border border-slate-200 overflow-hidden">
                              <Image
                                src={product.images[0] || '/placeholder.svg'}
                                alt={product.title}
                                fill
                                className="object-contain p-2"
                              />
                            </div>
                            <div className="text-center">
                              <h3 className="font-semibold text-sm line-clamp-2 mb-1">
                                {product.title}
                              </h3>
                              <p className="text-xs text-slate-500">{product.manufacturer}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFromComparison(product.id)}
                              className="absolute top-2 right-2"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Price Row */}
                    <ComparisonRow
                      label="Price"
                      values={comparedProducts.map(p => `$${p.price.toFixed(2)}`)}
                      compareType="lowest"
                      className="bg-blue-50/50"
                    />

                    {/* Basic Info */}
                    <ComparisonRow
                      label="Part Number"
                      values={comparedProducts.map(p => p.partNumber)}
                    />
                    <ComparisonRow
                      label="Category"
                      values={comparedProducts.map(p => p.category)}
                    />
                    <ComparisonRow
                      label="Subcategory"
                      values={comparedProducts.map(p => p.subcategory)}
                    />
                    <ComparisonRow
                      label="Package Type"
                      values={comparedProducts.map(p => p.packageType)}
                      compareType="exact"
                    />

                    {/* Stock & Availability */}
                    <tr className="bg-slate-50">
                      <td colSpan={comparedProducts.length + 1} className="px-6 py-2 text-xs font-semibold text-slate-700">
                        AVAILABILITY
                      </td>
                    </tr>
                    <ComparisonRow
                      label="Stock Status"
                      values={comparedProducts.map(p => p.stock)}
                      compareType="exact"
                      className="bg-green-50/30"
                    />
                    <ComparisonRow
                      label="MOQ"
                      values={comparedProducts.map(p => `${p.moq} units`)}
                      compareType="lowest"
                    />
                    <ComparisonRow
                      label="Lead Time"
                      values={comparedProducts.map(p => p.leadTime)}
                    />

                    {/* Compliance */}
                    <tr className="bg-slate-50">
                      <td colSpan={comparedProducts.length + 1} className="px-6 py-2 text-xs font-semibold text-slate-700">
                        COMPLIANCE
                      </td>
                    </tr>
                    <ComparisonRow
                      label="RoHS Compliant"
                      values={comparedProducts.map(p => p.rohs ? 'Yes' : 'No')}
                      compareType="boolean"
                    />
                    <ComparisonRow
                      label="Lifecycle Status"
                      values={comparedProducts.map(p => p.lifecycle)}
                      compareType="exact"
                    />

                    {/* Technical Specifications */}
                    <tr className="bg-slate-50">
                      <td colSpan={comparedProducts.length + 1} className="px-6 py-2 text-xs font-semibold text-slate-700">
                        TECHNICAL SPECIFICATIONS
                      </td>
                    </tr>
                    {getAllSpecKeys(comparedProducts).map((specKey) => (
                      <ComparisonRow
                        key={specKey}
                        label={specKey}
                        values={comparedProducts.map(p => 
                          p.specifications[specKey] ? String(p.specifications[specKey]) : '—'
                        )}
                        compareType="exact"
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Footer Actions - Mobile */}
          <div className="lg:hidden border-t border-slate-200 p-4 bg-white">
            <GradientButton
              variant="blue-red"
              className="w-full"
            >
              View Full Comparison Table
            </GradientButton>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}

// Helper Components
function SpecRow({ label, value, highlighted }: { label: string; value: string; highlighted?: boolean }) {
  return (
    <div className={cn(
      "flex justify-between py-1.5 border-b border-slate-100",
      highlighted && "bg-blue-50/50"
    )}>
      <span className="text-slate-600 font-medium">{label}:</span>
      <span className="text-slate-900 font-semibold">{value}</span>
    </div>
  )
}

interface ComparisonRowProps {
  label: string
  values: string[]
  compareType?: 'lowest' | 'highest' | 'exact' | 'boolean'
  className?: string
}

function ComparisonRow({ label, values, compareType, className }: ComparisonRowProps) {
  const getBestValueIndices = () => {
    if (!compareType || values.length === 0) return []

    switch (compareType) {
      case 'lowest': {
        const numericValues = values.map(v => parseFloat(v.replace(/[^0-9.-]/g, '')))
        const min = Math.min(...numericValues.filter(n => !isNaN(n)))
        return numericValues.map((v, i) => v === min ? i : -1).filter(i => i !== -1)
      }
      case 'highest': {
        const numericValues = values.map(v => parseFloat(v.replace(/[^0-9.-]/g, '')))
        const max = Math.max(...numericValues.filter(n => !isNaN(n)))
        return numericValues.map((v, i) => v === max ? i : -1).filter(i => i !== -1)
      }
      case 'exact': {
        const uniqueValues = new Set(values)
        return uniqueValues.size === 1 ? [] : []
      }
      case 'boolean': {
        return values.map((v, i) => v.toLowerCase() === 'yes' ? i : -1).filter(i => i !== -1)
      }
      default:
        return []
    }
  }

  const bestIndices = getBestValueIndices()
  const allSame = new Set(values).size === 1

  return (
    <tr className={cn("border-b border-slate-100", className)}>
      <td className="sticky left-0 bg-white px-6 py-3 text-sm font-medium text-slate-700 z-10">
        {label}
      </td>
      {values.map((value, index) => {
        const isBest = bestIndices.includes(index)
        const isDifferent = !allSame && compareType === 'exact'

        return (
          <td
            key={index}
            className={cn(
              "px-4 py-3 text-sm text-center",
              isBest && "bg-green-50 font-semibold text-green-700",
              isDifferent && value !== values[0] && "bg-yellow-50"
            )}
          >
            <div className="flex items-center justify-center gap-2">
              {isBest && <Check className="h-4 w-4 text-green-600" />}
              {isDifferent && value === '—' && <Minus className="h-4 w-4 text-slate-400" />}
              <span>{value}</span>
            </div>
          </td>
        )
      })}
    </tr>
  )
}

function getAllSpecKeys(products: any[]): string[] {
  const allKeys = new Set<string>()
  products.forEach(product => {
    Object.keys(product.specifications || {}).forEach(key => allKeys.add(key))
  })
  return Array.from(allKeys).sort()
}
